import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listEvents, deleteEvent, updateEvent } from "../../../api/cms";
import { slugify } from "../../../utils/slugify";

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    listEvents()
      .then((items) =>
        setEvents(
          [...items].sort((a, b) => {
            const da = new Date(b.dateTime || b.dateTimeFrom);
            const db = new Date(a.dateTime || a.dateTimeFrom);
            return da - db;
          })
        )
      )
      .catch(() => setError("Failed to load events"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await deleteEvent(id);
    load();
  };

  const handleToggleVisible = async (ev) => {
    const isVisible = ev.visible !== false;
    await updateEvent(ev.id, { ...ev, visible: !isVisible });
    setEvents((prev) =>
      prev.map((e) => (e.id === ev.id ? { ...e, visible: !isVisible } : e))
    );
  };

  if (loading) return <p>Loading…</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Events</h1>
        <Link to="/admin/events/new" className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90">
          + Add event
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Location</th>
              <th className="px-4 py-3 font-semibold">Visible</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {events.map((ev) => {
              const isVisible = ev.visible !== false;
              return (
                <tr key={ev.id}>
                  <td className="px-4 py-3 font-medium max-w-xs">
                    <span className="flex items-center gap-2 truncate">
                      {ev.title}
                      <a
                        href={`/events/${ev.id}/${slugify(ev.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                        title="View on website"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                      </a>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {ev.dateTime
                      ? new Date(ev.dateTime).toLocaleDateString("en-GB")
                      : ev.dateTimeFrom
                      ? `${new Date(ev.dateTimeFrom).toLocaleDateString("en-GB")} – ${new Date(ev.dateTimeTo).toLocaleDateString("en-GB")}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{ev.location}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleVisible(ev)}
                      className={`text-xs px-2 py-1 rounded-full font-semibold ${
                        isVisible
                          ? "bg-green-100 text-green-700 hover:bg-green-200"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      {isVisible ? "Visible" : "Hidden"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    <Link to={`/admin/events/${ev.id}/edit`} className="text-blue-600 hover:underline">Edit</Link>
                    <button onClick={() => handleDelete(ev.id)} className="text-red-500 hover:underline">Delete</button>
                  </td>
                </tr>
              );
            })}
            {events.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No events yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
